import React, { useEffect, useState, useRef } from "react";
import FadeInWrapper from "./fade-in-wrapper";

interface DraggableBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  showOverlay?: boolean;
  mainText?: string;
  subText?: string;
  buttonText?: string;
  onButtonClick?: () => void;
  disableDrag?: boolean;
  dragThreshold?: number; // 드래그 임계값 (기본값: 100px)
  closeOnDrag?: boolean; // 드래그로 닫기 가능 여부 (기본값: true)
}

const DraggableBottomSheet: React.FC<DraggableBottomSheetProps> = ({
  isOpen,
  onClose,
  children,
  showOverlay = true,
  mainText,
  subText,
  buttonText,
  onButtonClick,
  disableDrag = false,
  dragThreshold = 100,
  closeOnDrag = true,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragCurrentY, setDragCurrentY] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const bottomSheetRef = useRef<HTMLDivElement>(null);

  // 드래그 이벤트 핸들러
  const handleDragStart = (e: React.TouchEvent | React.MouseEvent) => {
    if (disableDrag || !closeOnDrag) return;

    e.preventDefault();
    setIsDragging(true);
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    setDragStartY(clientY);
    setDragCurrentY(clientY);
  };

  const handleDragMove = (e: React.TouchEvent | React.MouseEvent) => {
    if (!isDragging || disableDrag || !closeOnDrag) return;

    e.preventDefault();
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    setDragCurrentY(clientY);
  };

  const handleDragEnd = (e?: React.TouchEvent | React.MouseEvent) => {
    if (!isDragging || disableDrag || !closeOnDrag) return;

    if (e) e.preventDefault();

    const dragDistance = dragCurrentY - dragStartY;

    if (dragDistance > dragThreshold) {
      // 임계값 달성 시 닫기 애니메이션 시작
      setIsClosing(true);
      setIsDragging(false);

      // 애니메이션 완료 후 실제로 닫기
      setTimeout(() => {
        onClose();
        setIsClosing(false);
        setDragStartY(0);
        setDragCurrentY(0);
      }, 300);
    } else {
      // 원래 위치로 되돌리기
      setIsDragging(false);
      setDragStartY(0);
      setDragCurrentY(0);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      document.body.style.overflow = "hidden";
      // 약간의 지연을 두어 DOM이 렌더링된 후 애니메이션 시작
      setTimeout(() => {
        setIsVisible(true);
      }, 10);
    } else {
      setIsVisible(false);
      document.body.style.overflow = "unset";
      // 애니메이션이 끝난 후 컴포넌트 언마운트
      setTimeout(() => {
        setShouldRender(false);
      }, 300);
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!shouldRender) return null;

  return (
    <>
      {showOverlay && (
        <div
          className={`fixed inset-0 bg-black z-[60] transition-opacity duration-300 max-w-[742px] mx-auto ${
            isVisible ? "bg-opacity-50" : "bg-opacity-0"
          }`}
          onClick={onClose}
        />
      )}
      <div
        ref={bottomSheetRef}
        className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-[70] shadow-lg transform max-w-[742px] mx-auto ${
          isVisible ? "translate-y-0" : "translate-y-full"
        } ${!isDragging && !isClosing ? "transition-all duration-300" : ""}`}
        style={{
          transform: isDragging
            ? `translateY(${Math.max(0, dragCurrentY - dragStartY)}px)`
            : isClosing
            ? "translateY(100%)"
            : isVisible
            ? "translateY(0)"
            : "translateY(100%)",
        }}
      >
        {/* 드래그 핸들 */}
        <div
          className={`w-full flex justify-center py-4 select-none ${
            closeOnDrag && !disableDrag ? "cursor-pointer touch-none" : ""
          }`}
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
          onMouseDown={handleDragStart}
          onMouseMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
        >
          <div className="w-12 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* 바텀시트 내용 */}
        <div
          className="p-4 max-h-[70vh] overflow-y-auto custom-scrollbar"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "#d1d5db #f3f4f6",
          }}
        >
          {mainText && (
            <FadeInWrapper>
              <h1 className="text-2xl font-bold">{mainText}</h1>
            </FadeInWrapper>
          )}
          {subText && (
            <FadeInWrapper>
              <p className="text-md text-gray-500">{subText}</p>
            </FadeInWrapper>
          )}
          {children}
          {buttonText && (
            <button
              className="mt-4 w-full bg-blue-500 text-white font-bold py-4 px-4 rounded-xl"
              onClick={onButtonClick}
            >
              {buttonText}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default DraggableBottomSheet;
